import React, { useMemo, useRef, useState } from 'react';

import { Input, List, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import {
  AppState,
  userApi,
  UserMenu,
  setLeftMenuFiltersToggled,
  setLeftMenuToggled,
  selectLeftMenuFiltersToggled,
  useAppSelector,
} from 'core/lib';
import { selectUserFilteredMenu } from 'core/lib/modules/users/store';
import { fuzzyWordsSearch } from 'core/lib/utils';
import { Routes } from 'routes/routes';
import { useMatchParamId } from 'utils/hooks';
import LoaderWrapper from 'utils/loader-wrapper';

import UserMenuItem from './menuItem';

const mapState = (state: AppState) => ({
  filtersToggled: selectLeftMenuFiltersToggled(state),
});

const mapDispatch = {
  toggleLeftMenu: setLeftMenuToggled,
  toggleLeftMenuFilters: setLeftMenuFiltersToggled,
};

const connector = connect(mapState, mapDispatch);
type UserMenuProps = ConnectedProps<typeof connector> & RouteComponentProps;

const UserLeftMenu = ({ toggleLeftMenuFilters, filtersToggled }: UserMenuProps) => {
  const { t } = useTranslation();
  const id = useMatchParamId(Routes.USER_DETAILS.path);
  const [search, setSearch] = useState('');
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = userApi.useGetUserMenuQuery();

  const filteredMenuData = useAppSelector((state: AppState) => selectUserFilteredMenu(state, data));

  const searchResultData: UserMenu[] | undefined = useMemo(() => {
    return filteredMenuData?.filter((user) => fuzzyWordsSearch(search, user.firstName));
  }, [search, filteredMenuData]);

  const onFiltersToggleClick = () => {
    toggleLeftMenuFilters(!filtersToggled);
  };

  return (
    <LoaderWrapper loading={!data?.length && isLoading} message={t('loaders:user.loadingMenu')}>
      <div ref={searchContainerRef}>
        <div className="flex flex-col p-4 gap-4">
          <div className="flex items-center justify-between gap-4">
            {t('global:filters.toggleFilters')}
            <Switch
              onClick={onFiltersToggleClick}
              size="small"
              title={t('global:filters.toggleFilters')}
              checked={filtersToggled}
            />
          </div>
          <Input
            allowClear
            placeholder={t('global:placeholders.searchByName')}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        </div>
      </div>
      <AutoSizer>
        {({ width, height }) => (
          <List>
            <FixedSizeList<UserMenu[]>
              itemData={searchResultData}
              overscanCount={5}
              itemSize={40}
              itemCount={searchResultData?.length ?? 0}
              width={width}
              height={height - (searchContainerRef.current?.clientHeight ?? 0)}
            >
              {(props) => <UserMenuItem id={id} isLoading={isLoading} {...props} />}
            </FixedSizeList>
          </List>
        )}
      </AutoSizer>
    </LoaderWrapper>
  );
};

export default connector(UserLeftMenu);
